<script setup lang="ts">
import type { CommonFooterCollapsedProperties, FooterLink } from '@optionway/models';

import { Lock } from 'lucide-vue-next';

const prefix = 'components.common.footer';

const { renew } = useCookieConsent();
const properties = withDefaults(defineProps<CommonFooterCollapsedProperties>(), { hideAlma: undefined });

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.commonFooterCollapsed;
const currencyStore = useCurrencyStore();

const componentConfig = computed<CommonFooterCollapsedProperties>(() => ({
  hideAlma: properties.hideAlma ?? config?.hideAlma ?? false,
}));

const collapsedFooterLinks: FooterLink[] = [
  {
    type: 'link',
    label: `${prefix}.termsFull`,
    to: 'terms',
    target: '_blank',
    localeAware: true,
  },
  {
    type: 'link',
    label: `${prefix}.legalNotice`,
    to: 'legal-notices',
    target: '_blank',
    localeAware: true,
  },
  {
    type: 'link',
    label: `${prefix}.privacyPolicy`,
    to: 'privacy-policy',
    target: '_blank',
    localeAware: true,
  },
  {
    type: 'link',
    label: `${prefix}.cookiesPreferences`,
    action: renew,
  },
  {
    type: 'link',
    label: `${prefix}.accessibilityNonCompliant`,
    to: 'accessibility',
    target: '_blank',
    localeAware: true,
  },
];
</script>

<template>
  <div class="flex w-full flex-col gap-4 md:grid md:grid-cols-1 md:gap-8 lg:gap-14">
    <div class="flex flex-col gap-4 md:order-1 md:flex-row md:justify-between md:gap-8">
      <CommonFooterStarsRating :linkable="false" />
      <hr class="md:hidden" />
      <span class="flex h-fit items-center gap-2 text-base font-medium"
        ><Lock class="h-4 w-4" />{{ $t(`${prefix}.securePayment`) }}</span
      >
      <div class="flex h-fit items-center gap-8">
        <SvgoCommonFooterVisa
          class="h-3 w-9"
          filled
          :font-controlled="false"
          aria-hidden="true"
        /><SvgoCommonFooterMastercard
          class="h-3 w-5"
          filled
          :font-controlled="false"
          aria-hidden="true"
        /><SvgoCommonFooterAmex
          class="h-3 w-10"
          filled
          :font-controlled="false"
          aria-hidden="true"
        /><SvgoCommonFooterAncv
          class="h-3 w-5"
          filled
          :font-controlled="false"
          aria-hidden="true"
        /><SvgoCommonFooterAlma
          v-if="!componentConfig.hideAlma && currencyStore.isEuro"
          class="h-7 w-auto"
          filled
          :font-controlled="false"
          role="img"
          :aria-label="$t('components.common.footer.alma')"
        />
      </div>
      <hr class="md:hidden" />
    </div>

    <ul
      class="flex flex-col gap-2 text-secondary-foreground md:order-2 md:col-span-full md:flex-row md:justify-between md:gap-4"
    >
      <li
        v-for="(link, index) in collapsedFooterLinks"
        :key="`${link.label}-${index}`"
      >
        <template v-if="link.type === 'link'">
          <button
            v-if="link.action"
            type="button"
            class="inline-flex cursor-pointer items-center gap-2 text-left text-sm font-normal underline"
            @click="link.action()"
          >
            {{ $t(link.label) }}
            <component
              :is="resolveLucideIcon(link.icon)"
              v-if="link.icon"
              class="size-4"
              aria-hidden="true"
            />
          </button>
          <NuxtLinkLocale
            v-else-if="link.localeAware"
            :to="link.to"
            :target="link.target"
            :title="link.title"
            class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal underline"
          >
            {{ $t(link.label) }}
            <component
              :is="resolveLucideIcon(link.icon)"
              v-if="link.icon"
              class="size-4"
              aria-hidden="true"
            />
          </NuxtLinkLocale>
          <NuxtLink
            v-else
            :to="link.to"
            :target="link.target"
            :title="link.title"
            class="inline-flex cursor-pointer items-center gap-2 text-sm font-normal underline"
          >
            {{ $t(link.label) }}
            <component
              :is="resolveLucideIcon(link.icon)"
              v-if="link.icon"
              class="size-4"
              aria-hidden="true"
            />
          </NuxtLink>
        </template>
      </li>
    </ul>
  </div>
</template>
